import { useState, useEffect } from 'react';

interface UseModalStateProps {
  closeOnBreakpoint?: number;
}

/**
 * Custom hook to manage modal open/close state
 * Handles auto-close on resize
 * CONTEXT: We'll hardcode the breakpoint to 1280px for now since we're designing this to be able
 * to be moved to a different frontend in the future and don't want to rely too much on external dependencies.
 */
const useModalState = ({ closeOnBreakpoint = 1280 }: UseModalStateProps = {}): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
} => {
  const [isOpen, setIsOpen] = useState(false);

  // Close modal on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= closeOnBreakpoint && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, closeOnBreakpoint]);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return {
    isOpen,
    open,
    close,
  };
};

export default useModalState;
